import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import ImageUpload from '../../components/admin/ImageUpload';
import FileUpload from '../../components/admin/FileUpload';
import { getImageUrl } from '../../lib/utils';

const FacultyManagement = () => {
  const [items, setItems] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    title_ar: '',  // الصفة
    title_en: '',
    position_ar: '',  // المنصب
    position_en: '',
    degree_ar: '',  // الدرجة العلمية
    degree_en: '',
    academic_qualification_ar: '',  // المؤهل الأكاديمي
    academic_qualification_en: '',
    bio_ar: '',  // نبذة مختصرة
    bio_en: '',
    specialization_ar: '',  // التخصص
    specialization_en: '',
    college_id: '',
    image: '',
    cv_pdf: '',  // السيرة الذاتية PDF
    email: '',
    phone: '',
    is_published: true,
    order: 0
  });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => { 
    fetchItems();
    fetchColleges();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/faculty/all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      setItems(await response.json());
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchColleges = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/colleges/all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      setColleges(await response.json());
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editing ? `${BACKEND_URL}/api/faculty/${editing}` : `${BACKEND_URL}/api/faculty/`;
      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        fetchItems();
        resetForm();
        alert('تم الحفظ بنجاح! ✅');
      } else {
        alert('حدث خطأ أثناء الحفظ');
      }
    } catch (error) {
      console.error(error);
      alert('حدث خطأ');
    }
  };

  const handleEdit = (member) => {
    setEditing(member.id);
    setFormData({
      name_ar: member.name_ar || '',
      name_en: member.name_en || '',
      title_ar: member.title_ar || '',
      title_en: member.title_en || '',
      position_ar: member.position_ar || '',
      position_en: member.position_en || '',
      degree_ar: member.degree_ar || '',
      degree_en: member.degree_en || '',
      academic_qualification_ar: member.academic_qualification_ar || '',
      academic_qualification_en: member.academic_qualification_en || '',
      bio_ar: member.bio_ar || '',
      bio_en: member.bio_en || '',
      specialization_ar: member.specialization_ar || '',
      specialization_en: member.specialization_en || '',
      college_id: member.college_id || '',
      image: member.image || '',
      cv_pdf: member.cv_pdf || '',
      email: member.email || '',
      phone: member.phone || '',
      is_published: member.is_published !== undefined ? member.is_published : true,
      order: member.order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (member) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا العضو؟')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/faculty/${member.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        fetchItems();
        alert('تم الحذف بنجاح!');
      }
    } catch (error) {
      console.error(error);
      alert('حدث خطأ');
    }
  };

  const resetForm = () => {
    setFormData({
      name_ar: '',
      name_en: '',
      title_ar: '',
      title_en: '',
      position_ar: '',
      position_en: '',
      degree_ar: '',
      degree_en: '',
      academic_qualification_ar: '',
      academic_qualification_en: '',
      bio_ar: '',
      bio_en: '',
      specialization_ar: '',
      specialization_en: '',
      college_id: '',
      image: '',
      cv_pdf: '',
      email: '',
      phone: '',
      is_published: true,
      order: 0
    });
    setEditing(null);
    setShowForm(false);
  };

  const getCollegeName = (collegeId) => {
    const college = colleges.find(c => c.id === collegeId);
    return college ? college.name_ar : 'غير محدد';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-[#0F388E] flex items-center gap-3">
          <Users className="w-10 h-10" />
          أعضاء هيئة التدريس
        </h1>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="bg-[#0F388E]">
            <Plus className="ml-2 w-5 h-5" />
            إضافة عضو
          </Button>
        )}
      </div>

      {!showForm && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(member => (
            <Card key={member.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                {member.image && (
                  <img 
                    src={getImageUrl(member.image)} 
                    alt={member.name_ar} 
                    className="w-full h-56 object-cover rounded-lg mb-4"
                  />
                )}
                <h3 className="text-xl font-bold text-[#0F388E] mb-1">{member.name_ar}</h3>
                {member.title_ar && (
                  <p className="text-sm text-[#C9A040] font-semibold mb-2">{member.title_ar}</p>
                )}
                {member.position_ar && (
                  <p className="text-sm text-gray-600 mb-2">{member.position_ar}</p>
                )}
                {member.specialization_ar && (
                  <p className="text-sm text-gray-500 mb-3">التخصص: {member.specialization_ar}</p>
                )}
                
                <div className="space-y-1 mb-4 text-sm">
                  {member.college_id && (
                    <div className="flex items-center gap-2">
                      <span className="text-[#C9A040]">🏛️</span>
                      <span className="text-gray-700">{getCollegeName(member.college_id)}</span>
                    </div>
                  )}
                  {member.email && (
                    <div className="flex items-center gap-2">
                      <span className="text-[#C9A040]">✉️</span>
                      <span className="text-gray-700 text-xs">{member.email}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 justify-end border-t pt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(member)}
                    className="text-[#0F388E]"
                  >
                    <Edit2 className="w-4 h-4 ml-1" />
                    تعديل
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(member)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 ml-1" />
                    حذف
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {items.length === 0 && (
            <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">لا يوجد أعضاء هيئة تدريس مضافين بعد</p>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-6 h-6" />
              {editing ? 'تعديل عضو هيئة التدريس' : 'إضافة عضو هيئة التدريس'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg space-y-4 border border-blue-100">
                <h3 className="font-bold text-[#0F388E] text-lg">المعلومات الأساسية</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      اسم الدكتور بالعربي *
                    </label>
                    <input
                      type="text"
                      value={formData.name_ar}
                      onChange={(e) => setFormData({...formData, name_ar: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="د. محمد أحمد"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Doctor Name in English
                    </label>
                    <input
                      type="text"
                      value={formData.name_en}
                      onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="Dr. Mohammed Ahmed"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الكلية التابع لها
                  </label>
                  <select
                    value={formData.college_id}
                    onChange={(e) => setFormData({...formData, college_id: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  >
                    <option value="">اختر الكلية</option>
                    {colleges.map(college => (
                      <option key={college.id} value={college.id}>
                        {college.name_ar}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Academic Info */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg space-y-4 border border-green-100">
                <h3 className="font-bold text-[#0F388E] text-lg">المعلومات الأكاديمية</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الصفة بالعربي * (مثال: أستاذ دكتور)
                    </label>
                    <input
                      type="text"
                      value={formData.title_ar}
                      onChange={(e) => setFormData({...formData, title_ar: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="أستاذ دكتور"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title in English
                    </label>
                    <input
                      type="text"
                      value={formData.title_en}
                      onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="Professor"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الدرجة العلمية بالعربي *
                    </label>
                    <input
                      type="text"
                      value={formData.degree_ar}
                      onChange={(e) => setFormData({...formData, degree_ar: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="دكتوراه"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Academic Degree in English
                    </label>
                    <input
                      type="text"
                      value={formData.degree_en}
                      onChange={(e) => setFormData({...formData, degree_en: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="PhD"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      المؤهل الأكاديمي بالعربي
                    </label>
                    <input
                      type="text"
                      value={formData.academic_qualification_ar}
                      onChange={(e) => setFormData({...formData, academic_qualification_ar: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="دكتوراه في طب الأسنان"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Academic Qualification in English
                    </label>
                    <input
                      type="text"
                      value={formData.academic_qualification_en}
                      onChange={(e) => setFormData({...formData, academic_qualification_en: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="PhD in Dentistry"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      المنصب بالعربي *
                    </label>
                    <input
                      type="text"
                      value={formData.position_ar}
                      onChange={(e) => setFormData({...formData, position_ar: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="أستاذ طب الأسنان"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Position in English
                    </label>
                    <input
                      type="text"
                      value={formData.position_en}
                      onChange={(e) => setFormData({...formData, position_en: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="Professor of Dentistry"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      التخصص بالعربي
                    </label>
                    <input
                      type="text"
                      value={formData.specialization_ar}
                      onChange={(e) => setFormData({...formData, specialization_ar: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="جراحة الفم والأسنان"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Specialization in English
                    </label>
                    <input
                      type="text"
                      value={formData.specialization_en}
                      onChange={(e) => setFormData({...formData, specialization_en: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="Oral and Maxillofacial Surgery"
                    />
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg space-y-4 border border-purple-100">
                <h3 className="font-bold text-[#0F388E] text-lg">نبذة مختصرة</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      نبذة مختصرة بالعربي
                    </label>
                    <textarea
                      value={formData.bio_ar}
                      onChange={(e) => setFormData({...formData, bio_ar: e.target.value})}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="نبذة مختصرة عن الدكتور وخبراته..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Short Bio in English
                    </label>
                    <textarea
                      value={formData.bio_en}
                      onChange={(e) => setFormData({...formData, bio_en: e.target.value})}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="Brief biography about the doctor..."
                    />
                  </div>
                </div>
              </div>

              {/* Files */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-lg space-y-4 border border-amber-100">
                <h3 className="font-bold text-[#0F388E] text-lg">الصورة والملفات</h3>
                <ImageUpload
                  label="صورة الدكتور"
                  value={formData.image}
                  onChange={(v) => setFormData({...formData, image: v})}
                />
                <FileUpload
                  label="السيرة الذاتية (PDF)"
                  value={formData.cv_pdf}
                  onChange={(v) => setFormData({...formData, cv_pdf: v})}
                  accept=".pdf"
                />
              </div>

              {/* Contact */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-lg space-y-4 border border-cyan-100">
                <h3 className="font-bold text-[#0F388E] text-lg">معلومات التواصل</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      البريد الإلكتروني
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="doctor@alnafisuni.edu.ye"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      رقم الهاتف
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="+967 XXX XXX XXX"
                    />
                  </div>
                </div>
              </div>

              {/* Settings */}
              <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                <h3 className="font-bold text-[#0F388E] text-lg">الإعدادات</h3>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_published"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                    className="w-5 h-5 text-[#0F388E] border-gray-300 rounded focus:ring-[#0F388E]"
                  />
                  <label htmlFor="is_published" className="text-gray-700">
                    نشر العضو على الموقع
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>
                  إلغاء
                </Button>
                <Button type="submit" className="bg-[#0F388E]">
                  {editing ? 'تحديث العضو' : 'حفظ العضو'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FacultyManagement;
